<template>
  <Teleport to="body" :disabled="!teleportToBody">
    <div
      v-bind="attrsWithoutStyle"
      v-if="modelValue"
      class="ui-popup"
      :class="[{ 'ui-popup--content-only': allowBackgroundInteraction }, modalClass]"
      :style="rootPopupStyle"
      ref="popupRoot"
      tabindex="-1"
      @mousedown="handleBringToFront"
      @focusin="handleBringToFront"
    >
      <div v-if="!noDim" class="ui-popup__backdrop" @click="closeOnBackdropClick && handleClose()"></div>
      <div
        ref="popupContent"
        class="ui-popup__content"
        :class="[
          `ui-popup--${size}`,
          { 'ui-popup--centered': centered && !isFullscreen },
          { 'ui-popup--right': position === 'right' && !isFullscreen },
          { 'ui-popup--left': position === 'left' && !isFullscreen },
          { 'ui-popup--no-dim': noDim },
          { 'ui-popup--fullscreen': isFullscreen },
          { 'ui-popup--no-scroll': isScroll },
          { 'ui-popup--show-scrollbar': showScrollbar },
          { 'ui-popup--floating': isFloating }, // 플로팅 모달 전용 클래스 추가
          { 'ui-popup--animated': hasAnimated }, // 이미 애니메이션 완료된 경우
        ]"
        :style="modalStyle"
        @click="isFloating && emit('activate-modal')"
      >
        <!-- 왼쪽 리사이즈 핸들러 (오른쪽 고정 모드) -->
        <div
          v-if="position === 'right' && !isFullscreen && !isFloating"
          class="ui-popup__resize-handle ui-popup__resize-handle--left"
          @mousedown="e => handleResizeStart('left', e)"
        ></div>

        <!-- 오른쪽 리사이즈 핸들러 (왼쪽 고정 모드) -->
        <div
          v-if="position === 'left' && !isFullscreen && !isFloating"
          class="ui-popup__resize-handle ui-popup__resize-handle--right"
          @mousedown="e => handleResizeStart('right', e)"
        ></div>

        <!-- 플로팅 모드 리사이즈 핸들러들 -->
        <template v-if="isFloating && !isFullscreen">
          <!-- 오른쪽 리사이즈 핸들러 -->
          <div class="ui-popup__resize-handle ui-popup__resize-handle--right" @mousedown="e => handleResizeStart('right', e)"></div>
          <!-- 아래쪽 리사이즈 핸들러 -->
          <div class="ui-popup__resize-handle ui-popup__resize-handle--bottom" @mousedown="e => handleResizeStart('bottom', e)"></div>
          <!-- 오른쪽 아래 모서리 리사이즈 핸들러 -->
          <div class="ui-popup__resize-handle ui-popup__resize-handle--corner" @mousedown="e => handleResizeStart('corner', e)"></div>
        </template>
        <div class="ui-popup__header" :class="{ 'ui-popup__header--draggable': allowFloating }" @mousedown="allowFloating && handleDragStart($event)">
          <div class="flex items-center gap-5">
            <h3 class="ui-popup__title">
              <slot name="title">
                {{ title }}
                <!-- 아이콘 설정 -->
                <span
                  class="icon-new"
                  v-if="data.activityUnreadCnt > 0"
                  @click.stop="
                    data.activityUnreadCnt = 0;
                    $emit('title-icon-click', data.planId);
                  "
                  style="cursor: pointer"
                ></span>
              </slot>
            </h3>
          </div>
          <div class="ui-popup__header-actions">
            <slot name="headerActions-right"></slot>
            <div class="ui-popup__actions flex gap-5">
              <template v-if="true">
                <button
                  v-if="showFullscreenButton"
                  class="ui-popup__fullscreen"
                  @click="toggleFullscreen"
                  :title="isFullscreen ? t('common.modal.exitFullscreen') : t('common.modal.fullscreen')"
                >
                  <Icon :name="isFullscreen ? 'heroicons:arrows-pointing-in' : 'heroicons:arrows-pointing-out'" size="18" />
                </button>
                <!-- 플로팅 모드일 때 크기 버튼 추가 -->
                <!-- 기본/커스텀일 때만: 넓은 크기(1/2)로 전환 -->
                <button
                  v-if="position === 'right' && showSizeButtons && !isFloating && !isFullscreen && sizeMode === 'default'"
                  class="ui-popup__size-btn"
                  @click="changeSize('half')"
                  :title="t('common.modal.wideSize')"
                >
                  <!-- 사용자 정의 SVG - 2개로 분할된 화면, 왼쪽만 강조됨 -->
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="2" y="4" width="9" height="16" fill="currentColor" />
                    <rect x="13" y="4" width="9" height="16" fill="none" stroke="currentColor" stroke-width="1.5" />
                  </svg>
                </button>
                <!-- 넓은 크기/커스텀일 때만: 기본 크기(1/3)로 전환 -->
                <button
                  v-if="position === 'right' && showSizeButtons && !isFloating && !isFullscreen && (sizeMode === 'half' || sizeMode === 'custom')"
                  class="ui-popup__size-btn"
                  @click="changeSize('default')"
                  :title="t('common.modal.defaultSize')"
                >
                  <!-- 사용자 정의 SVG - 3개로 분할된 화면, 왼쪽만 채워짐 -->
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="2" y="4" width="6" height="16" fill="currentColor" />
                    <rect x="10" y="4" width="6" height="16" fill="none" stroke="currentColor" stroke-width="1.5" />
                    <rect x="18" y="4" width="4" height="16" fill="none" stroke="currentColor" stroke-width="1.5" />
                  </svg>
                </button>
                <button v-if="showCloseButton || isFloating" class="ui-popup__close" :title="t('common.action.close')" @click="handleClose">
                  <Icon name="heroicons:x-mark" size="20" />
                </button>
              </template>
            </div>
          </div>
        </div>
        <div
          ref="popupBody"
          class="ui-popup__body"
          :style="{
            padding: `${
              size === 'small'
                ? '0px'
                : size === 'xmedium'
                  ? '12px'
                  : size === 'medium'
                    ? '16px'
                    : size === 'large'
                      ? '20px'
                      : size === 'xlarge'
                        ? '24px'
                        : '24px'
            }`,
          }"
          @scroll="handleBodyScroll"
        >
          <slot></slot>
        </div>
        <div v-if="showFooter" class="ui-popup__footer">
          <slot name="footerActions"></slot>
        </div>
        <!-- 스크롤 탑 버튼 -->
        <Transition name="fade">
          <button
            v-if="showScrollTopButton && (position === 'right' || position === 'left' || isFullscreen)"
            class="ui-popup__scroll-top"
            @click="scrollToTop"
            :title="t('common.modal.scrollTop')"
          >
            <Icon name="heroicons:chevron-up" size="20" />
          </button>
        </Transition>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch, inject, provide, nextTick, useAttrs } from 'vue';
import { useRoute } from 'vue-router';
import { useModalStore } from '~/composables/useModalStore';
import { showConfirmDialog } from '~/composables/useConfirmDialog';
import { dismissOpenOverlays } from '~/composables/useOverlayDismiss';

defineOptions({
  inheritAttrs: false,
});

// 전역 스크롤 락 상태 (모달 여러 개일 때를 대비한 카운트 기반)
let scrollLockCount = 0;
let scrollTopBeforeLock = 0;

function shouldApplyBodyScrollLock() {
  return props.bodyScrollLock && !props.allowBackgroundInteraction;
}

function lockBodyScroll() {
  if (typeof window === 'undefined') return;
  if (scrollLockCount === 0) {
    scrollTopBeforeLock = window.scrollY || window.pageYOffset || 0;
    const body = document.body;
    body.style.position = 'fixed';
    body.style.top = `-${scrollTopBeforeLock}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    document.documentElement.style.overflowY = 'hidden';
  }
  scrollLockCount += 1;
}

function unlockBodyScroll() {
  if (typeof window === 'undefined') return;
  scrollLockCount = Math.max(0, scrollLockCount - 1);
  if (scrollLockCount === 0) {
    const body = document.body;
    const top = body.style.top;
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    document.documentElement.style.overflowY = '';

    // body.style.top은 음수 픽셀 문자열이므로, 이를 다시 스크롤 위치로 복원
    if (top) {
      const offset = parseInt(top, 10) || 0;
      window.scrollTo(0, -offset);
    }
  }
}

// 라우트 감시
const route = useRoute();
const { t } = useI18n();

// ===== Esc로 모달 only topMost 닫기 위한 글로벌 모달 스택 =====
const modalUID = Math.random().toString(36).slice(2);
const popupRoot = ref(null);
const popupContent = ref(null);

// 모달 스택 store 사용
const { modalStack, pushModalStack, popModalStack, isTopModal, bringToFront } = useModalStore();

// 포커스 or 마우스다운되면 최상위로 올리기
function handleBringToFront(event) {
  bringToFront(modalUID);
}
// ===========================================================

// 플로팅 팝업 카운터 관리 - 전역 상태로 사용하기 위해 기본값을 제공
const floatingPopupCount = inject('floatingPopupCount', ref(0));

// 고정 팝업 카운터 관리 - 전역 상태로 사용하기 위해 기본값을 제공
const fixedPopupCount = inject('fixedPopupCount', ref(0));

// 현재 팝업이 플로팅 상태인지 추적
const isThisPopupFloating = ref(false);

// 현재 팝업의 플로팅 상태를 다른 컴포넌트에서 사용할 수 있도록 제공
provide('isModalFloating', isThisPopupFloating);

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '',
  },
  size: {
    type: String,
    default: 'medium',
    validator: value => ['small', 'xmedium', 'medium', 'large', 'mlarge', 'xlarge', 'xxlarge', 'full'].includes(value),
  },
  centered: {
    type: Boolean,
    default: true,
  },
  showCloseButton: {
    type: Boolean,
    default: true,
  },
  showFullscreenButton: {
    type: Boolean,
    default: true,
  },
  closeOnBackdropClick: {
    type: Boolean,
    default: true,
  },
  position: {
    type: String,
    default: 'center',
    validator: value => ['center', 'right', 'left'].includes(value),
  },
  noDim: {
    type: Boolean,
    default: false,
  },
  /** false면 document body 스크롤 락 생략. allowBackgroundInteraction=true면 자동으로 락하지 않음 */
  bodyScrollLock: {
    type: Boolean,
    default: true,
  },
  showFooter: {
    type: Boolean,
    default: false,
  },
  isScroll: {
    type: Boolean,
    default: false,
  },
  /** true이면 모달 본문 스크롤바 표시 (기본은 숨김) */
  showScrollbar: {
    type: Boolean,
    default: false,
  },
  showSizeButtons: {
    type: Boolean,
    default: false,
  },
  allowBackgroundInteraction: {
    type: Boolean,
    default: false,
  },
  /**
   * true이면 no-dim·배경 상호작용 우측 패널에서 패널 밖 클릭 시 닫힘.
   * 기본 false — 일반 우측 모달은 바깥 클릭으로 닫히지 않음.
   */
  closeOnOutsideClick: {
    type: Boolean,
    default: false,
  },
  allowFloating: {
    type: Boolean,
    default: false,
  },
  /** true이면 열릴 때부터 플로팅(떼어낸) 상태로 연다. allowFloating 과 position="right"/"left" 와 함께 쓴다. */
  defaultFloating: {
    type: Boolean,
    default: false,
  },
  /** defaultFloating 일 때 시작 위치를 오른쪽·아래로 어긋나게 하는 픽셀 값. 여러 팝업을 겹치지 않게 열 때 쓴다. */
  floatingOffset: {
    type: Number,
    default: 0,
  },
  /** defaultFloating 일 때 열릴 때의 너비를 화면 너비의 비율(0~1)로 정한다. 기본은 1/3. */
  floatingWidthRatio: {
    type: Number,
    default: 1 / 3,
  },
  /** defaultFloating 일 때 열릴 때의 높이를 화면 높이의 비율(0~1)로 정한다. 없으면 내용에 맞춰 최대 600px. */
  floatingHeightRatio: {
    type: Number,
    default: null,
  },
  /**
   * true이면 중앙(center 등 left/right 아님) 모달에서 헤더를 어느 방향으로든 소폭 드래그해도 플로팅 전환.
   * 기본 false — 기존(좌측 50px·롱프레스) 동작 유지. 좌·우 고정 패널 분기는 이 prop과 무관.
   */
  floatOnAnyDrag: {
    type: Boolean,
    default: false,
  },
  height: {
    type: [String, Number],
    default: null,
  },
  zIndex: {
    type: Number,
    default: null,
  },
  data: {
    type: Object,
    default: () => ({}),
  },
  beforeClose: {
    type: Function,
    default: null,
  },
  checkTextChanges: {
    type: Boolean,
    default: true,
  },
  modalClass: {
    type: [String, Array, Object],
    default: '',
  },
  /**
   * true이면 루트를 document.body로 Teleport.
   * 모달(오버레이)은 루트에 마운트하는 것이 일반적.
   * 특히, 상위 모달( transform 애니메이션 등 ) 안에서 열릴 때 position:fixed가 뷰포트가 아닌 조상 기준이 되어
   * 높이/배치가 달라지는 것을 막는다.
   */
  teleportToBody: {
    type: Boolean,
    default: false,
  },
  /**
   * false면 우측 패널 폭의 localStorage(modalSizeMode·modalCustomWidth)를 읽지 않고 저장하지도 않음.
   * 보조 모달(보고서 KPI/OKR 등)이 메인 우측 패널 사용자 설정과 섞이지 않게 할 때 사용.
   */
  persistModalSizePreference: {
    type: Boolean,
    default: true,
  },
  /** true이면 모달 오픈 시 전체화면으로 시작 */
  defaultFullscreen: {
    type: Boolean,
    default: false,
  },
});

// emits에 'title-icon-click' 이벤트 추가
const emit = defineEmits(['update:modelValue', 'floating-changed', 'activate-modal', 'title-icon-click']);

const isFullscreen = ref(false);
const customWidth = ref(null);
const customHeight = ref(null);
const sizeMode = ref('default'); // 'default', 'half', 'full'
const isFloating = ref(false);
const dragPosition = ref({ x: 0, y: 0 });
const hasAnimated = ref(false); // 모달이 처음 열릴 때만 애니메이션 적용
const showScrollTopButton = ref(false); // 스크롤 탑 버튼 표시 여부

// TinyMCE 내용 비교 시: 저장 콘텐츠가 아니라 편집기 내부 DOM만 바뀐 경우를 동일로 본다.
// - data-mce-* (선택 경계 data-mce-selected 등), bogus br
// - 빈 단락 placeholder(<p><br...></p>)는 의미 없는 값으로 동일 취급
// - 선택/포커스 시 끼는 U+FEFF·U+200B 등 보이지 않는 문자
function normalizeTinymceHtml(html) {
  if (!html) return '';
  let result = String(html);

  result = result.replace(/<br[^>]*data-mce-bogus[^>]*>/gi, '');

  // data-mce-* 속성 제거(큰따옴표/작은따옴표; 한 태그에 여러 개 있을 수 있어 반복)
  let prev;
  do {
    prev = result;
    result = result.replace(/\s+data-mce-[a-z0-9_-]+\s*=\s*"[^"]*"/gi, '');
    result = result.replace(/\s+data-mce-[a-z0-9_-]+\s*=\s*'[^']*'/gi, '');
  } while (prev !== result);

  // TinyMCE/브라우저가 선택 경계에 넣는 문자(실제 본문과 무관)
  result = result.replace(/[\uFEFF\u200B-\u200D\u2060]/g, '');

  // 빈 단락에서만 br 태그 무시(일반 본문 줄바꿈 <br>는 유지)
  result = result.replace(/<p>\s*<br[^>]*>\s*<\/p>/gi, '<p></p>');
  result = result.replace(/<p>\s*<\/p>/gi, '');
  result = result.trim();
  return result;
}

// 텍스트 입력 필드 변경사항 추적
const initialTextValues = ref(new Map());
const popupBody = ref(null);

// 스크롤 이벤트 핸들러 (스크롤 탑 버튼 표시/숨김)
function handleBodyScroll(e) {
  const scrollTop = e.target.scrollTop;
  showScrollTopButton.value = scrollTop > 200; // 200px 이상 스크롤 시 버튼 표시
}

// 스크롤 탑 함수
function scrollToTop() {
  if (popupBody.value) {
    popupBody.value.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }
}

// 모달 내부의 UiInput, UiTextarea, QuillEditor, TinyMCE 초기값 저장
function captureInitialTextValues() {
  if (!popupBody.value) return;

  initialTextValues.value.clear();

  // UiInput 찾기 (readonly 제외 순번으로 인덱싱 - hasTextChanges와 동일)
  const inputs = popupBody.value.querySelectorAll('.ui-input__field');
  let inputIndex = 0;
  inputs.forEach(input => {
    const uiInput = input.closest('.ui-input');
    if (uiInput) {
      const readonly = input.hasAttribute('readonly') || input.getAttribute('readonly') === 'true';
      if (!readonly) {
        const key = `input-${inputIndex}`;
        initialTextValues.value.set(key, input.value || '');
        inputIndex++;
      }
    }
  });

  // UiTextarea 찾기 (readonly 제외 순번으로 인덱싱)
  const textareas = popupBody.value.querySelectorAll('.ui-textarea__field');
  let textareaIndex = 0;
  textareas.forEach(textarea => {
    const uiTextarea = textarea.closest('.ui-textarea');
    if (uiTextarea) {
      const readonly = textarea.hasAttribute('readonly') || textarea.getAttribute('readonly') === 'true';
      if (!readonly) {
        const key = `textarea-${textareaIndex}`;
        initialTextValues.value.set(key, textarea.value || '');
        textareaIndex++;
      }
    }
  });

  // QuillEditor 찾기 (readonly 제외 순번으로 인덱싱)
  const quillEditors = popupBody.value.querySelectorAll('.ql-editor');
  let quillIndex = 0;
  quillEditors.forEach(editor => {
    const quillWrapper = editor.closest('.quill-editor-wrapper');
    if (quillWrapper) {
      // readonly인 경우 contenteditable="false"로 설정됨
      const readonly = editor.getAttribute('contenteditable') === 'false';
      if (!readonly) {
        const key = `quill-${quillIndex}`;
        initialTextValues.value.set(key, editor.innerHTML || '');
        quillIndex++;
      }
    }
  });

  // TinyMCE Editor 찾기 (readonly 제외 순번으로 인덱싱)
  // - TinymceEditor는 @tinymce/tinymce-vue 기반이라 iframe 내부 body에 실제 내용이 들어감
  // - 모달 DOM 안에서 .tox-edit-area iframe 을 기준으로 조회
  const tinymceIframes = popupBody.value.querySelectorAll('.tox-edit-area iframe');
  let tinymceIndex = 0;
  tinymceIframes.forEach(iframe => {
    const tinymceWrapper = iframe.closest('.tox-tinymce');
    if (!tinymceWrapper) return;

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;
    const body = doc.body;
    if (!body) return;

    // readonly인 경우 body가 편집 불가 상태(contenteditable="false")
    const readonly = body.getAttribute('contenteditable') === 'false' || body.isContentEditable === false;
    if (readonly) return;

    const key = `tinymce-${tinymceIndex}`;
    initialTextValues.value.set(key, body.innerHTML || '');
    tinymceIndex++;
  });
}

/** 지정 컨테이너 내 입력 필드만 baseline 갱신 (나머지 영역 dirty 감지 유지) */
function updateInitialTextValuesInContainer(containerSelector) {
  if (!popupBody.value || !containerSelector) return;

  const container = popupBody.value.querySelector(containerSelector);
  if (!container) return;

  const inputs = popupBody.value.querySelectorAll('.ui-input__field');
  let inputIndex = 0;
  inputs.forEach(input => {
    const uiInput = input.closest('.ui-input');
    if (uiInput) {
      const readonly = input.hasAttribute('readonly') || input.getAttribute('readonly') === 'true';
      if (!readonly) {
        if (container.contains(input)) {
          initialTextValues.value.set(`input-${inputIndex}`, input.value || '');
        }
        inputIndex++;
      }
    }
  });

  const textareas = popupBody.value.querySelectorAll('.ui-textarea__field');
  let textareaIndex = 0;
  textareas.forEach(textarea => {
    const uiTextarea = textarea.closest('.ui-textarea');
    if (uiTextarea) {
      const readonly = textarea.hasAttribute('readonly') || textarea.getAttribute('readonly') === 'true';
      if (!readonly) {
        if (container.contains(textarea)) {
          initialTextValues.value.set(`textarea-${textareaIndex}`, textarea.value || '');
        }
        textareaIndex++;
      }
    }
  });
}

/**
 * 컨테이너 안 행 순서만 바뀐 경우, 현재 값으로 baseline을 덮어쓰지 않고
 * 기존 baseline 값을 새 순번에 맞게 재배치한다. (내용 수정 dirty는 유지)
 */
function permuteInitialTextValuesInContainer(containerSelector, fromIds, toIds) {
  if (!popupBody.value || !containerSelector) return;
  if (!Array.isArray(fromIds) || !Array.isArray(toIds)) return;
  if (fromIds.length === 0 || fromIds.length !== toIds.length) return;

  const container = popupBody.value.querySelector(containerSelector);
  if (!container) return;

  const oldIndexById = new Map(fromIds.map((id, i) => [String(id), i]));
  const oldRowOfNew = toIds.map(id => oldIndexById.get(String(id)));
  if (oldRowOfNew.some(i => i == null)) return;

  const permuteCollected = (selector, closestClass, prefix) => {
    const nodes = popupBody.value.querySelectorAll(selector);
    let index = 0;
    const listKeys = [];

    nodes.forEach(node => {
      const wrapper = node.closest(closestClass);
      if (!wrapper) return;
      const readonly = node.hasAttribute('readonly') || node.getAttribute('readonly') === 'true';
      if (readonly) return;
      if (container.contains(node)) {
        listKeys.push(`${prefix}-${index}`);
      }
      index++;
    });

    const rowCount = fromIds.length;
    if (listKeys.length === 0 || listKeys.length % rowCount !== 0) return;

    const fieldsPerRow = listKeys.length / rowCount;
    const snapshot = listKeys.map(k => initialTextValues.value.get(k) || '');

    for (let newRow = 0; newRow < rowCount; newRow++) {
      const oldRow = oldRowOfNew[newRow];
      for (let f = 0; f < fieldsPerRow; f++) {
        initialTextValues.value.set(listKeys[newRow * fieldsPerRow + f], snapshot[oldRow * fieldsPerRow + f]);
      }
    }
  };

  permuteCollected('.ui-input__field', '.ui-input', 'input');
  permuteCollected('.ui-textarea__field', '.ui-textarea', 'textarea');
}

// 텍스트 입력 필드에 변경사항이 있는지 확인
function hasTextChanges() {
  if (!popupBody.value || initialTextValues.value.size === 0) return false;

  // UiInput 확인
  const inputs = popupBody.value.querySelectorAll('.ui-input__field');
  let inputIndex = 0;
  for (const input of inputs) {
    const uiInput = input.closest('.ui-input');
    if (uiInput) {
      const readonly = input.hasAttribute('readonly') || input.getAttribute('readonly') === 'true';
      if (!readonly) {
        const key = `input-${inputIndex}`;
        const initialValue = initialTextValues.value.get(key) || '';
        const currentValue = input.value || '';
        if (initialValue !== currentValue) {
          return true;
        }
        inputIndex++;
      }
    }
  }

  // UiTextarea 확인
  const textareas = popupBody.value.querySelectorAll('.ui-textarea__field');
  let textareaIndex = 0;
  for (const textarea of textareas) {
    const uiTextarea = textarea.closest('.ui-textarea');
    if (uiTextarea) {
      const readonly = textarea.hasAttribute('readonly') || textarea.getAttribute('readonly') === 'true';
      if (!readonly) {
        const key = `textarea-${textareaIndex}`;
        const initialValue = initialTextValues.value.get(key) || '';
        const currentValue = textarea.value || '';
        if (initialValue !== currentValue) {
          return true;
        }
        textareaIndex++;
      }
    }
  }

  // QuillEditor 확인
  const quillEditors = popupBody.value.querySelectorAll('.ql-editor');
  let quillIndex = 0;
  for (const editor of quillEditors) {
    const quillWrapper = editor.closest('.quill-editor-wrapper');
    if (quillWrapper) {
      // readonly인 경우 contenteditable="false"로 설정됨
      const readonly = editor.getAttribute('contenteditable') === 'false';
      if (!readonly) {
        const key = `quill-${quillIndex}`;
        const initialValue = initialTextValues.value.get(key) || '';
        const currentValue = editor.innerHTML || '';
        if (initialValue !== currentValue) {
          return true;
        }
        quillIndex++;
      }
    }
  }

  // TinyMCE Editor 확인
  const tinymceIframes = popupBody.value.querySelectorAll('.tox-edit-area iframe');
  let tinymceIndex = 0;
  for (const iframe of tinymceIframes) {
    const tinymceWrapper = iframe.closest('.tox-tinymce');
    if (!tinymceWrapper) continue;

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) continue;
    const body = doc.body;
    if (!body) continue;

    const readonly = body.getAttribute('contenteditable') === 'false' || body.isContentEditable === false;
    if (readonly) continue;

    const key = `tinymce-${tinymceIndex}`;
    // 초기 캡처 시점에 iframe이 아직 생성되지 않은 경우를 위해, initial 값이 없으면 스킵
    if (!initialTextValues.value.has(key)) {
      tinymceIndex++;
      continue;
    }

    const initialValue = initialTextValues.value.get(key) || '';
    const currentValue = body.innerHTML || '';

    const normalizedInitial = normalizeTinymceHtml(initialValue);
    const normalizedCurrent = normalizeTinymceHtml(currentValue);
    if (normalizedInitial !== normalizedCurrent) {
      return true;
    }
    tinymceIndex++;
  }

  return false;
}

// 모달 닫기 처리 함수 (단일 경로로 통합)
async function handleClose() {
  if (!props.modelValue) return;

  // 텍스트 입력 필드 변경사항 확인
  const hasChanges = props.checkTextChanges && hasTextChanges();

  if (hasChanges) {
    // 변경사항이 있으면 확인 팝업 표시
    return new Promise(resolve => {
      showConfirmDialog(
        t('common.confirm'),
        t('common.modal.unsavedCloseConfirm'),
        async () => {
          initialTextValues.value.clear();
          // beforeClose 함수가 있으면 실행
          if (props.beforeClose) {
            try {
              const result = await props.beforeClose();
              if (result !== false) {
                emit('update:modelValue', false);
              }
            } catch (error) {
              // 에러 발생 시 닫지 않음
            }
          } else {
            emit('update:modelValue', false);
          }
          resolve(true);
        },
        () => {
          resolve(false);
        },
      );
    });
  }

  // 변경사항이 없으면 기존 beforeClose 로직 실행
  if (props.beforeClose) {
    try {
      const result = await props.beforeClose();
      // false를 반환하면 닫지 않음
      if (result === false) {
        return;
      }
    } catch (error) {
      // 에러가 발생하면 닫지 않음
      return;
    }
  }

  // beforeClose가 없거나 true를 반환하면 닫기
  emit('update:modelValue', false);
}

// ESC로 닫기 위한 콜백 함수 (store에서 호출됨)
async function closeModal() {
  await handleClose();
}

/** closeOnOutsideClick: no-dim 우측 패널만 패널 밖 클릭 시 닫기 (최상위 모달만) */
let outsideClickListenerAttached = false;

function handleDocumentPointerDown(event) {
  if (!props.modelValue) return;
  if (!props.closeOnOutsideClick) return;
  if (!isTopModal(modalUID)) return;

  const content = popupContent.value;
  if (content?.contains(event.target)) return;

  handleClose();
}

function attachOutsideClickListener() {
  if (outsideClickListenerAttached) return;
  if (!props.closeOnOutsideClick) return;

  setTimeout(() => {
    if (!props.modelValue) return;
    if (!props.closeOnOutsideClick) return;
    document.addEventListener('mousedown', handleDocumentPointerDown, true);
    outsideClickListenerAttached = true;
  }, 0);
}

function detachOutsideClickListener() {
  if (!outsideClickListenerAttached) return;
  document.removeEventListener('mousedown', handleDocumentPointerDown, true);
  outsideClickListenerAttached = false;
}

// isFloating watch 추가
watch(isFloating, newValue => {
  isThisPopupFloating.value = newValue;
  emit('floating-changed', newValue);
});

const screenWidth = ref(window.innerWidth);

/** 플로팅 모달이 뷰포트(visual viewport) 안에만 오도록 top/left 클램프 */
function clampFloatingPositionToViewport(x, y) {
  if (typeof window === 'undefined') return { x, y };
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const el = popupContent.value;
  if (!el) return { x, y };

  const rect = el.getBoundingClientRect();
  const w = rect.width || el.offsetWidth;
  const h = rect.height || el.offsetHeight;
  if (!w && !h) return { x, y };

  const minX = Math.min(0, vw - w);
  const maxX = Math.max(0, vw - w);
  const minY = Math.min(0, vh - h);
  const maxY = Math.max(0, vh - h);

  return {
    x: Math.min(Math.max(x, minX), maxX),
    y: Math.min(Math.max(y, minY), maxY),
  };
}

const handleResize = () => {
  screenWidth.value = window.innerWidth;
  if (isFloating.value) {
    const c = clampFloatingPositionToViewport(dragPosition.value.x, dragPosition.value.y);
    dragPosition.value = { x: c.x, y: c.y };
  }
};

/** height prop을 CSS 길이 문자열로 */
const heightPropToCss = () => {
  if (props.height == null || props.height === '') return null;
  return typeof props.height === 'number' ? `${props.height}px` : String(props.height).trim();
};

/**
 * 플로팅 전환 시: 고정 모달에서 쓰던 height(예: 100vh)가 인라인에서 빠지면
 * content-only의 :has([style*='height']) + 본문 flex(height:0) 조합으로 본문 높이가 붕괴됨.
 * 또한 매우 큰 뷰포트 높이는 드래그용 플로팅에선 65vh 전후가 보기 좋아 기본을 나눔.
 */
const floatingHeightStyle = () => {
  if (customHeight.value != null) {
    const h = `${customHeight.value}px`;
    // 원래 패널형(대략 전체 높이) 모달이면 플로팅 리사이즈 시에도 65vh 미만으로 쪼그라들지 않게
    const m = heightPropToCss()?.match(/^([\d.]+)\s*vh\s*$/i);
    const wasTallVh = m && parseFloat(m[1]) >= 80;
    return { height: h, maxHeight: h, minHeight: wasTallVh ? '65vh' : undefined };
  }
  const raw = heightPropToCss();
  if (!raw) {
    return { height: undefined, maxHeight: '600px', minHeight: undefined };
  }
  const m = raw.match(/^([\d.]+)\s*vh\s*$/i);
  if (m) {
    const vh = parseFloat(m[1]);
    if (vh >= 80) {
      return {
        height: '65vh',
        minHeight: '65vh',
        maxHeight: '100vh',
      };
    }
    if (vh >= 65) {
      return {
        height: raw,
        minHeight: '65vh',
        maxHeight: '100vh',
      };
    }
  }
  return { height: raw, maxHeight: raw, minHeight: undefined };
};

// 루트 .ui-popup의 scoped SCSS z-index(1000)보다 우선해 중첩 모달이 부모 위에 오도록 함
// 외부 :style(CSS 변수 등)은 rootPopupStyle에 덮이지 않도록 병합
const attrs = useAttrs();
const attrsWithoutStyle = computed(() => {
  const { style: _style, ...rest } = attrs;
  return rest;
});
const rootPopupStyle = computed(() => {
  const attrStyle = attrs.style;
  const normalized = attrStyle && typeof attrStyle === 'object' && !Array.isArray(attrStyle) ? { ...attrStyle } : {};
  if (props.zIndex !== null && props.zIndex !== undefined) {
    normalized.zIndex = props.zIndex;
  }
  return normalized;
});

const modalStyle = computed(() => {
  const resolveZIndex = styleObj => {
    if (props.zIndex !== null && props.zIndex !== undefined) {
      styleObj.zIndex = props.zIndex;
    }
    return styleObj;
  };

  if (isFullscreen.value) {
    return resolveZIndex({});
  }

  if (isFloating.value) {
    const style = {
      position: 'fixed',
      top: `${dragPosition.value.y}px`,
      left: `${dragPosition.value.x}px`,
      // 페이지/모달 전용 SCSS의 inset:0 등과 겹치면 right·bottom이 남아 박스가 양쪽에 고정되어
      // 시각적 이동이 마우스의 절반처럼 보임 → 플로팅은 top/left만 사용
      right: 'auto',
      bottom: 'auto',
      margin: 0,
      transform: 'none',
      minWidth: 'auto',
    };

    // 너비가 설정된 경우
    if (customWidth.value) {
      style.width = `${customWidth.value}px`;
    } else {
      style.width = props.position === 'right' || props.position === 'left' ? `${screenWidth.value / 3}px` : undefined;
    }

    const fh = floatingHeightStyle();
    style.height = fh.height;
    style.maxHeight = fh.maxHeight;
    if (fh.minHeight) style.minHeight = fh.minHeight;

    return resolveZIndex(style);
  }

  if (customWidth.value && (props.position === 'right' || props.position === 'left')) {
    return resolveZIndex({ width: `${customWidth.value}px` });
  }

  if (props.position === 'right') {
    if (sizeMode.value === 'half') {
      return resolveZIndex({ width: `${screenWidth.value / 2}px` });
    }
    if (sizeMode.value === 'default') {
      return resolveZIndex({ width: `${screenWidth.value / 3}px` });
    }
    // custom인데 폭이 없는 불일치(예: 과거 LS에 custom만 저장된 경우) → 기본 1/3과 동일 처리
    if (sizeMode.value === 'custom') {
      return resolveZIndex({ width: `${screenWidth.value / 3}px` });
    }
  }

  if (props.position === 'left') {
    if (sizeMode.value === 'default' || sizeMode.value === 'custom') {
      return resolveZIndex({ width: `${screenWidth.value / 3}px` });
    }
  }

  const style = {};

  // 높이 설정
  if (props.height) {
    const heightValue = typeof props.height === 'number' ? `${props.height}px` : props.height;
    style.height = heightValue;
    style.maxHeight = heightValue;
  }

  return resolveZIndex(style);
});

let isResizing = false;
let resizeDirection = null;
let startX = 0;
let startY = 0;
let startWidth = 0;
let startHeight = 0;
let modalElement = null;

// modelValue 감시: 열릴 때 stack에 추가, 닫힐 때 stack에서 제거
watch(
  () => props.modelValue,
  async newValue => {
    if (newValue) {
      // 배경에 열린 Select/MultiSelect/DatePicker 드롭다운 닫기
      dismissOpenOverlays();
      // 처음부터 떼어낸(플로팅) 상태로 열기: 화면 가운데에 너비 1/3 크기로 띄운다. (플로팅 기본 너비와 같다)
      // 높이는 내용에 따라 달라지므로 플로팅 최대 높이(600px)를 기준으로 세로 가운데를 잡는다.
      if (props.defaultFloating && props.allowFloating && !props.defaultFullscreen) {
        const width = Math.round(window.innerWidth * props.floatingWidthRatio);
        // 높이를 비율로 정하면 그 높이로 열고, 아니면 내용에 맞춰 최대 600px(플로팅 기본)로 둔다.
        const height = props.floatingHeightRatio ? Math.round(window.innerHeight * props.floatingHeightRatio) : 600;
        customWidth.value = width;
        if (props.floatingHeightRatio) {
          customHeight.value = height;
        }
        dragPosition.value = {
          x: Math.max(0, Math.round((window.innerWidth - width) / 2)) + props.floatingOffset,
          y: Math.max(16, Math.round((window.innerHeight - height) / 2)) + props.floatingOffset,
        };
        isFloating.value = true;
      }
      if (props.defaultFullscreen) {
        isFullscreen.value = true;
        sizeMode.value = 'full';
      }
      // stack 관리 (닫기 콜백과 함께 등록)
      await nextTick();
      pushModalStack(modalUID, closeModal);
      if (shouldApplyBodyScrollLock()) {
        lockBodyScroll();
      }
      // 띄운 후 바로 포커스(플로팅/다중 모달에서 tab포커싱 대비)
      await nextTick();
      // 포커스 시 브라우저가 스크롤을 맨 위로 옮기는 것 방지(중첩 모달·body lock 시 페이지 위치 유지)
      if (popupRoot.value) popupRoot.value.focus({ preventScroll: true });
      // 모달이 열릴 때 텍스트 입력 필드 초기값 저장 (즉시 1회)
      await nextTick();
      captureInitialTextValues();
      // TinyMCE는 비동기 로드라 iframe이 늦게 생김 → 준비된 뒤 초기값 재캡처
      setTimeout(() => {
        captureInitialTextValues();
      }, 800);
      // 애니메이션 완료 후 플래그 설정 (300ms = 애니메이션 시간)
      setTimeout(() => {
        hasAnimated.value = true;
      }, 300);
      attachOutsideClickListener();
    } else {
      detachOutsideClickListener();
      popModalStack(modalUID);
      if (shouldApplyBodyScrollLock()) {
        unlockBodyScroll();
      }
      // 모달이 닫힐 때 fullscreen 상태 초기화
      isFullscreen.value = false;
      sizeMode.value = 'default';
      customWidth.value = null;
      customHeight.value = null;
      isFloating.value = false;
      dragPosition.value = { x: 0, y: 0 };
      hasAnimated.value = false; // 애니메이션 플래그 초기화
      showScrollTopButton.value = false; // 스크롤 탑 버튼 초기화
      // 초기값 맵 초기화
      initialTextValues.value.clear();
    }
  },
  { immediate: true },
);

// 라우트 변경 시 모달 닫기
watch(
  () => route.fullPath,
  () => {
    if (props.modelValue) {
      // 변경사항 확인 없이 바로 닫기 (라우트 변경 시에는 확인 불필요)
      initialTextValues.value.clear();
      emit('update:modelValue', false);
    }
  },
);

onMounted(() => {
  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);
  window.addEventListener('resize', handleResize);

  // 혹시 엔트리에서 바로 열리는 경우도 대비
  if (props.modelValue) {
    pushModalStack(modalUID, closeModal);
    if (shouldApplyBodyScrollLock()) {
      lockBodyScroll();
    }
    // 띄운 후 포커스
    nextTick(() => {
      if (popupRoot.value) popupRoot.value.focus({ preventScroll: true });
    });
  }

  // sizeMode 복원: 'full' 은 isFullscreen 상태가 보존되지 않아 크기 버튼이 안 보이는 불일치가 생기므로 제외
  if (props.persistModalSizePreference) {
    const savedSizeMode = localStorage.getItem('modalSizeMode');
    if (savedSizeMode && savedSizeMode !== 'full') {
      sizeMode.value = savedSizeMode;
    } else {
      sizeMode.value = 'default';
    }
    if (sizeMode.value === 'custom') {
      const w = localStorage.getItem('modalCustomWidth');
      const parsed = w != null ? parseInt(w, 10) : NaN;
      if (!Number.isFinite(parsed) || parsed < 300) {
        sizeMode.value = 'default';
        localStorage.removeItem('modalCustomWidth');
        localStorage.setItem('modalSizeMode', 'default');
      } else {
        customWidth.value = parsed;
      }
    }
  } else {
    sizeMode.value = 'default';
  }
});

onUnmounted(() => {
  document.removeEventListener('mousemove', handleMouseMove);
  document.removeEventListener('mouseup', handleMouseUp);
  window.removeEventListener('resize', handleResize);
  detachOutsideClickListener();

  // unmount시 stack 처리
  popModalStack(modalUID);
});

function toggleFullscreen() {
  isFullscreen.value = !isFullscreen.value;

  if (isFullscreen.value) {
    customWidth.value = null;
    sizeMode.value = 'full';
    saveUserPreference();
  } else {
    sizeMode.value = 'default';
    saveUserPreference();
  }
}

function changeSize(mode) {
  if (isFullscreen.value && mode !== 'full') {
    isFullscreen.value = false;
  }

  sizeMode.value = mode;
  customWidth.value = null;

  saveUserPreference();
}

function saveUserPreference() {
  if (!props.persistModalSizePreference) return;
  localStorage.setItem('modalSizeMode', sizeMode.value);
  if (sizeMode.value === 'custom' && customWidth.value != null) {
    localStorage.setItem('modalCustomWidth', String(customWidth.value));
  } else {
    localStorage.removeItem('modalCustomWidth');
  }
}

function handleResizeStart(direction = 'left', e) {
  if (isFullscreen.value) return;

  isResizing = true;
  resizeDirection = direction;
  startX = e.clientX;
  startY = e.clientY;
  modalElement = e.target.closest('.ui-popup__content');
  startWidth = modalElement.offsetWidth;
  startHeight = modalElement.offsetHeight;

  // 이벤트 리스너 추가
  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);

  // 커서 스타일 설정
  if (direction === 'left' || direction === 'right') {
    document.body.style.cursor = 'ew-resize';
  } else if (direction === 'bottom') {
    document.body.style.cursor = 'ns-resize';
  } else if (direction === 'corner') {
    document.body.style.cursor = 'nwse-resize';
  }

  e.preventDefault();
}

function handleMouseMove(e) {
  if (!isResizing) return;

  const maxWidth = window.innerWidth;
  const maxHeight = window.innerHeight;
  const minWidth = 300;
  const minHeight = 200;

  // 리사이즈 방향에 따라 처리
  if (resizeDirection === 'left') {
    // 왼쪽 핸들 (오른쪽 고정 모드)
    const width = startWidth - (e.clientX - startX);
    if (width >= minWidth && width <= maxWidth) {
      customWidth.value = width;
      sizeMode.value = 'custom';
    }
  } else if (resizeDirection === 'right') {
    // 오른쪽 핸들 (플로팅 모드)
    const width = startWidth + (e.clientX - startX);
    if (width >= minWidth && width <= maxWidth) {
      customWidth.value = width;
    }
  } else if (resizeDirection === 'bottom') {
    // 아래쪽 핸들 (플로팅 모드)
    const height = startHeight + (e.clientY - startY);
    if (height >= minHeight && height <= maxHeight) {
      customHeight.value = height;
    }
  } else if (resizeDirection === 'corner') {
    // 모서리 핸들 (플로팅 모드) - 너비와 높이 동시 조절
    const width = startWidth + (e.clientX - startX);
    const height = startHeight + (e.clientY - startY);

    if (width >= minWidth && width <= maxWidth) {
      customWidth.value = width;
    }

    if (height >= minHeight && height <= maxHeight) {
      customHeight.value = height;
    }
  }
}

function handleMouseUp() {
  isResizing = false;
  resizeDirection = null;
  document.removeEventListener('mousemove', handleMouseMove);
  document.removeEventListener('mouseup', handleMouseUp);
  document.body.style.cursor = '';

  if (sizeMode.value === 'custom') {
    saveUserPreference();
  }

  // 콘솔에 디버깅 정보 출력
  // console.log('Resize complete', {
  //   width: customWidth.value,
  //   height: customHeight.value,
  //   floating: isFloating.value
  // });
}
// 드래그 관련 변수
let isDragging = false;
let startDragX = 0;
let startDragY = 0;
let offsetX = 0;
let offsetY = 0;
let initialModalPosition = { x: 0, y: 0 };
let longPressTimer = null; // 롱프레스 타이머
let maxFloatingX = 0; // 플로팅 중 최대 x 위치 추적 (left 패널 snap-back 조건용)

// 드래그 시작 핸들러
function handleDragStart(e) {
  // 헤더의 버튼 클릭 시 드래그 방지
  if (e.target.closest('.ui-popup__actions')) return;

  // 드래그 중 제목/배경 텍스트 블록 선택 방지
  e.preventDefault();
  if (typeof window !== 'undefined' && window.getSelection) {
    window.getSelection()?.removeAllRanges();
  }

  // 모달 활성화 이벤트 발생 (z-index 올리기 위해)
  emit('activate-modal');

  // 드래그 중 상태로 변경
  isDragging = true;
  maxFloatingX = 0;

  // 현재 모달의 위치 저장
  const modalRect = e.currentTarget.getBoundingClientRect();
  initialModalPosition = {
    x: modalRect.left,
    y: modalRect.top,
  };

  // 마우스 포인터 위치와 모달 위치의 차이 계산 (오프셋)
  offsetX = e.clientX - modalRect.left;
  offsetY = e.clientY - modalRect.top;

  // 이미 플로팅 모드인 경우
  if (isFloating.value) {
    // 현재 dragPosition을 기준으로 시작점 계산
    startDragX = e.clientX - dragPosition.value.x;
    startDragY = e.clientY - dragPosition.value.y;
  } else {
    // 고정 모드에서는 현재 모달 위치를 기준으로 시작점 계산
    startDragX = e.clientX - initialModalPosition.x;
    startDragY = e.clientY - initialModalPosition.y;
  }

  // 고정 모드일 때 500ms 롱프레스 시 플로팅 모드로 전환
  if (!isFloating.value) {
    clearTimeout(longPressTimer);
    longPressTimer = setTimeout(() => {
      if (isDragging && !isFloating.value) {
        // 현재 모달 위치를 플로팅 시작 위치로 설정 (제자리 유지)
        const next = clampFloatingPositionToViewport(initialModalPosition.x, initialModalPosition.y);
        dragPosition.value = { x: next.x, y: next.y };
        startDragX = e.clientX - dragPosition.value.x;
        startDragY = e.clientY - dragPosition.value.y;
        isFloating.value = true;
        emit('floating-changed', true);
      }
    }, 500);
  }

  // 이벤트 리스너 등록
  document.addEventListener('mousemove', handleDragMove);
  document.addEventListener('mouseup', handleDragEnd);
  document.body.style.cursor = 'move';
}

// 드래그 이동 핸들러
function handleDragMove(e) {
  if (!isDragging) return;

  e.preventDefault();

  // 오프셋을 고려한 위치 계산
  const newX = e.clientX - startDragX;
  const newY = e.clientY - startDragY;

  // 오른쪽 가장자리 근처로 드래그 시 고정 모드로 전환
  // - 좁은 창: 왼쪽 끝이 뷰포트 오른쪽 700px 띠 안(기존)
  // - 가로로 매우 넓은 창: 플로팅일 때만 오른쪽 끝 기준 스냅(우측 고정에선 newX+width가 항상 맞닿은 상태라 비플로팅이면 떼어내기 불가)
  const floatEl = popupContent.value;
  const floatW = floatEl ? floatEl.getBoundingClientRect().width : 0;
  const snapByLeftInRightStrip = isFloating.value && newX > screenWidth.value - 700;
  const snapByRightEdge = isFloating.value && floatW > 0 && newX + floatW >= screenWidth.value - 48;
  if (props.position === 'right' && fixedPopupCount.value == 0 && (snapByLeftInRightStrip || snapByRightEdge)) {
    // 플로팅 모드 해제 및 오른쪽 팝업으로 복귀
    isFloating.value = false;
    isDragging = false;
    dragPosition.value = { x: 0, y: 0 }; // 위치 초기화

    // 이벤트 발생
    emit('floating-changed', false);

    // 오른쪽 팝업으로 복귀
    sizeMode.value = 'default';
    customWidth.value = null;

    document.removeEventListener('mousemove', handleDragMove);
    document.removeEventListener('mouseup', handleDragEnd);
    document.body.style.cursor = '';
    return;
  }

  // 왼쪽 고정 패널: floating 중 최대 x 갱신
  if (props.position === 'left' && isFloating.value) {
    maxFloatingX = Math.max(maxFloatingX, newX);
  }

  // 왼쪽 고정 패널: 오른쪽으로 충분히 이동(maxFloatingX > 200) 후 왼쪽 가장자리 48px 이내로 복귀 시 도킹
  // maxFloatingX 조건으로 초기 위치(x≈0)에서 즉시 발동되는 것을 방지
  const snapByLeftEdge = isFloating.value && floatW > 0 && newX <= 48;
  if (props.position === 'left' && maxFloatingX > 200 && snapByLeftEdge) {
    isFloating.value = false;
    isDragging = false;
    dragPosition.value = { x: 0, y: 0 };
    maxFloatingX = 0;

    emit('floating-changed', false);

    sizeMode.value = 'default';
    customWidth.value = null;

    document.removeEventListener('mousemove', handleDragMove);
    document.removeEventListener('mouseup', handleDragEnd);
    document.body.style.cursor = '';
    return;
  }

  // 위치 업데이트 (뷰포트 내로 제한)
  const clamped = clampFloatingPositionToViewport(newX, newY);
  dragPosition.value = { x: clamped.x, y: clamped.y };

  // 플로팅 모드로 전환
  // - 오른쪽 고정 패널: 왼쪽으로 50px 이상 드래그 시
  // - 왼쪽 고정 패널: 오른쪽으로 50px 이상 드래그 시
  // - floatOnAnyDrag(기본 false): center에서만 어느 방향 소폭 이동. 미사용 시 기존(왼쪽 50px)과 동일
  const shouldFloat =  !isFloating.value && (props.position === 'left' ? e.clientX - initialModalPosition.x > 50 : props.position === 'right' || !props.floatOnAnyDrag ? initialModalPosition.x - e.clientX > 50 : Math.hypot(newX - initialModalPosition.x, newY - initialModalPosition.y) > 6);
  if (shouldFloat) {
    isFloating.value = true;

    // 이벤트 발생
    emit('floating-changed', true);
  }
}

// 드래그 종료 핸들러
function handleDragEnd() {
  isDragging = false;
  maxFloatingX = 0;
  clearTimeout(longPressTimer);
  longPressTimer = null;
  document.removeEventListener('mousemove', handleDragMove);
  document.removeEventListener('mouseup', handleDragEnd);
  document.body.style.cursor = '';
}

// 고정 모드 전환
function togglePinned() {
  isFloating.value = false;
  dragPosition.value = { x: 0, y: 0 };

  // 이벤트 발생
  emit('floating-changed', false);
}

// 플로팅 모드에서 오른쪽 팝업으로 붙이는 함수
function dockToRight(mode) {
  // 플로팅 모드 해제
  isFloating.value = false;

  // 위치 초기화
  dragPosition.value = { x: 0, y: 0 };

  // 오른쪽 팝업으로 설정
  sizeMode.value = mode;
  customWidth.value = null;

  // 이벤트 발생
  emit('floating-changed', false);

  // 사용자 설정 저장
  saveUserPreference();
}

// 초기 텍스트 값 초기화 함수 (외부 노출)
function clearInitialTextValues() {
  initialTextValues.value.clear();
}

// 외부에 노출할 함수 및 변수
defineExpose({
  clearInitialTextValues,
  captureInitialTextValues,
  updateInitialTextValuesInContainer,
  permuteInitialTextValuesInContainer,
});
</script>

<style lang="scss" scoped>
.ui-popup {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;

  &.type-task {
    .ui-popup__header,
    .ui-popup__body,
    .ui-popup__footer {
      box-sizing: border-box;
      padding: 16px !important;
    }
  }

  &--floating {
    animation: none !important; // 애니메이션 제거
    transition: none !important; // 전환 효과 제거

    // 플로팅 모달의 기본 스타일 설정
    position: fixed !important;

    // 고정 위치를 갖도록 설정
    top: auto;
    right: auto;
    bottom: auto;
    left: auto;

    // 모서리 둥글게
    border-radius: 8px !important;
  }

  &--content-only {
    position: static;
    top: auto;
    left: auto;
    right: auto;
    bottom: auto;

    .ui-popup__backdrop {
      display: none;
    }

    .ui-popup__content {
      position: fixed;
      z-index: 1000;

      /* max-height만 있을 때는 매칭하면 안 됨(플로팅 등) — 본문 height:0 레이아웃 붕괴 */
      &:has([style*='; height:']),
      &:has([style^='height:']) {
        .ui-popup__body {
          overflow-y: auto;
          height: 0;
          flex-grow: 1;
        }
      }
    }
  }

  &::-webkit-scrollbar {
    display: none;
  }

  &__backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    animation: fade-in $transition-normal forwards;
  }

  &__content {
    position: relative;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    margin: $spacing-xl;
    animation: scale-in $transition-normal forwards;
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - #{$spacing-xl * 2});
    width: 100%;

    // height prop이 지정된 모달은 본문이 콘텐츠 길이로 부모 높이를 밀지 않도록 고정 (max-height만으로는 매칭 제외)
    &[style*='; height:'],
    &[style^='height:'] {
      .ui-popup__body {
        min-height: 0;
        height: 0;
        flex: 1 1 auto;
        overflow-y: auto;
      }
    }
  }

  &__resize-handle {
    position: absolute;
    z-index: 10;

    &--left {
      left: 0;
      top: 0;
      bottom: 0;
      width: 5px;
      cursor: ew-resize;
    }

    &--right {
      right: 0;
      top: 0;
      bottom: 0;
      width: 5px;
      cursor: ew-resize;
    }

    &--bottom {
      left: 0;
      right: 0;
      bottom: 0;
      height: 5px;
      cursor: ns-resize;
    }

    &--corner {
      right: 0;
      bottom: 0;
      width: 15px;
      height: 15px;
      cursor: nwse-resize;

      &::before {
        content: '';
        position: absolute;
        right: 0;
        bottom: 0;
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 0 0 15px 15px;
        border-color: transparent transparent rgba(0, 0, 0, 0.2) transparent;
      }
    }

    &:hover {
      background-color: rgba(0, 0, 0, 0.1);

      &.ui-popup__resize-handle--corner {
        background-color: transparent;

        &::before {
          border-color: transparent transparent rgba(0, 0, 0, 0.3) transparent;
        }
      }
    }

    &:active {
      background-color: rgba(0, 0, 0, 0.2);

      &.ui-popup__resize-handle--corner {
        background-color: transparent;

        &::before {
          border-color: transparent transparent rgba(0, 0, 0, 0.4) transparent;
        }
      }
    }
  }

  &__header {
    display: flex;
    align-items: center;
    padding: $spacing-lg;
    border-bottom: 1px solid $border-color;

    &--draggable {
      cursor: move;
      position: relative;
      user-select: none;

      &:hover::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 40px;
        height: 4px;
        background-color: #ddd;
        border-radius: 2px;
        opacity: 0.7;
      }
    }
  }

  &__header-actions {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: $spacing-sm;
  }

  &__title {
    margin: 0;
    font-size: $font-size-xxl;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 500px; // 추천값
  }

  &__close,
  &__fullscreen,
  &__size-btn,
  &__pin-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background: transparent;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    color: #000;
    transition:
      background-color $transition-normal,
      color $transition-normal;

    &:hover {
      background-color: $hover-background;
      color: $text-color;
    }

    &.active {
      background-color: $hover-background;
      color: $primary-color;
    }
  }

  &__body {
    padding: $spacing-lg;
    flex-grow: 1;
    scrollbar-width: none;
    background-color: #fff;
    border-radius: 8px;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    /* 모바일 스크롤 개선 */

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__footer {
    padding: $spacing-lg;
    border-top: 1px solid $border-color;
    display: flex;
    justify-content: flex-end;
    gap: $spacing-sm;
  }

  // Sizes - 모든 해상도 대응 비율 기반
  &--small {
    width: 400px;
    max-width: calc(100vw - 40px);
  }

  &--xmedium {
    width: 440px;
    max-width: calc(100vw - 40px);
  }

  &--medium {
    width: 700px;
    max-width: calc(100vw - 40px);
  }

  &--large {
    width: 1200px;
    max-width: calc(100vw - 40px);
  }

  &--mlarge {
    width: 1400px;
    max-width: calc(100vw - 40px);
  }

  &--xlarge {
    width: 1600px;
    max-width: calc(100vw - 40px);
  }

  &--xxlarge {
    width: 1800px;
    max-width: calc(100vw - 40px);
  }

  // 새 목표 추가 모달 전용 사이즈
  &--goal-form {
    width: 768px;
    max-width: calc(100% - 40px);
  }

  &--full {
    max-width: calc(100vw - #{$spacing-xl * 2});
    height: calc(100vh - #{$spacing-xl * 2});
  }

  &--centered {
    margin: auto;
  }

  &--right {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    margin: 0;
    border-radius: 8px 0 0 8px;
    max-width: 100vw !important;
    min-width: 700px;
    height: 100vh;
    max-height: 100vh;
    animation: slide-in-right $transition-normal forwards;

    .ui-popup__body {
      min-height: 0;
      flex: 1 1 auto;
      overflow-y: auto;
    }
  }

  &--left {
    position: absolute;
    left: 0;
    right: auto;
    top: 0;
    bottom: 0;
    margin: 0;
    border-radius: 0 8px 8px 0;
    max-width: 100vw !important;
    min-width: 700px;
    height: 100vh;
    max-height: 100vh;
    animation: slide-in-left $transition-normal forwards;
  }

  &--no-dim {
    z-index: 1000;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  }

  &--fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    margin: 0;
    border-radius: 0;
    max-width: 100vw !important;
    max-height: 100vh !important;
    width: 100vw !important;
    height: 100vh !important;
    animation: fade-in $transition-normal forwards;
  }

  &--no-scroll {
    overflow: hidden;
  }

  &--show-scrollbar {
    .ui-popup__body {
      scrollbar-width: thin;
      scrollbar-color: rgba(200, 200, 200, 0.5) transparent;

      &::-webkit-scrollbar {
        display: block;
        width: 6px;
        height: 6px;
      }

      &::-webkit-scrollbar-track {
        background: transparent;
      }

      &::-webkit-scrollbar-thumb {
        background: rgba(200, 200, 200, 0.5);
        border-radius: 3px;

        &:hover {
          background: rgba(180, 180, 180, 0.7);
        }
      }
    }
  }

  // 이미 애니메이션이 완료된 경우 (전체화면 전환 시 애니메이션 방지)
  &--animated {
    &.ui-popup--right,
    &.ui-popup--left,
    &.ui-popup--fullscreen {
      animation: none;
    }
  }

  // 스크롤 탑 버튼
  &__scroll-top {
    position: absolute;
    bottom: 30px;
    right: 24px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: #333;
    color: white;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    transition: all 0.2s ease;
    z-index: 10;

    &:hover {
      background-color: #222;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    }

    &:active {
      transform: translateY(0);
    }
  }
}

// 스크롤 탑 버튼 페이드 애니메이션
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes fade-in {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes scale-in {
  from {
    transform: scale(0.95);
    opacity: 0;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes slide-in-right {
  from {
    transform: translateX(100%);
  }

  to {
    transform: translateX(0);
  }
}

@keyframes slide-in-left {
  from {
    transform: translateX(-100%);
  }

  to {
    transform: translateX(0);
  }
}

@media (max-width: 768px) {
  .ui-popup {
    &__content {
      margin: $spacing-md;
      max-height: calc(100vh - #{$spacing-md * 2});
    }

    &--full {
      max-width: calc(100vw - #{$spacing-md * 2});
      height: calc(100vh - #{$spacing-md * 2});
    }

    &--fullscreen {
      top: 16px;
      left: 16px;
      right: 16px;
      bottom: 16px;
      margin: 0;
      width: calc(100vw - 32px);
      height: calc(100vh - 32px);
      max-width: calc(100vw - 32px);
      max-height: calc(100vh - 32px);
    }
  }
}
</style>
